"use client";

import { Check, ChevronDown } from "lucide-react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { Sheet } from "@/components/ui/design-system/sheet";
import { Skeleton } from "@/components/ui/design-system/skeleton";
import { CONTROL_PRESS } from "@/components/ui/design-system/surface-styles";
import { Switcher } from "@/components/ui/design-system/switcher";
import { Hint, Strong } from "@/components/ui/design-system/text";
import { trpc } from "@/lib/trpc-client";
import { cn } from "@/lib/utils";
import type { CreatorPortalAccess } from "./creatorAuthContext";
import { DEPTH_ROOT_ID } from "./one/chrome";
import { ListCard, Row } from "./one/ui";
import { portalHref } from "./useCreatorPortalAccess";

/**
 * Names the brand whose portal the creator is currently in, and — for creators
 * on more than one — switches between them after the server checks membership.
 * Switching changes only the program scope in the URL.
 *
 * The control is the design system's `Switcher`: the identity tile, the
 * searchable list and the keyboard contract all come from there rather than
 * being built again here. This file owns the data and what a switch *does*.
 */
export function ProgramSwitcher({
  access,
  presentation = "popover",
  compact = false,
}: {
  access: CreatorPortalAccess | null | undefined;
  /**
   * Where this is being drawn, which decides its shape.
   *
   * A bottom edge means something on a phone: it is where the thumb is and
   * where the panel came from. At rail width there is no thumb and the list
   * belongs where the control is, so the same data gets a popover. The host
   * says which, rather than this measuring the window — the shell already
   * draws one chrome or the other at exactly that breakpoint, and a second
   * observer could disagree with it for a frame.
   */
  presentation?: "popover" | "sheet";
  /** The rail folded to icons: the program's tile alone. */
  compact?: boolean;
}) {
  const router = useRouter();
  const t = useTranslations("shell");
  const [switchError, setSwitchError] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const programsQuery = trpc.creatorPortal.getMyPrograms.useQuery(
    access ?? {},
    { enabled: !!access, retry: false },
  );

  const utils = trpc.useUtils();

  const switchProgram = trpc.creatorPortal.switchProgram.useMutation({
    onSuccess: (result) => {
      setSwitchError(null);
      setSheetOpen(false);
      // Everything cached under the old access belongs to the old brand — drop
      // it so no stale program's videos or docs flash during the transition.
      void utils.creatorPortal.invalidate();
      // Land on the home page of the new program: "your videos" and submission
      // slots are per-program, so keeping the current path would show a page
      // whose contents just silently changed underneath them.
      router.push(portalHref("/one", { orgSlug: result.orgSlug }));
      router.refresh();
    },
    // The server re-checks membership, so this legitimately fails when a
    // program is disabled or access is removed between opening the list and
    // choosing. Say so rather than letting the row quietly snap back.
    onError: (error) => setSwitchError(error.message),
  });

  const current = programsQuery.data?.current;
  const programs = programsQuery.data?.programs ?? [];

  // A shape, not nothing. Returning null left a hole at the foot of the rail
  // between the nav and the account row, so the one part of that column that
  // loads was also the one part that appeared out of nowhere.
  if (!current) {
    return programsQuery.isLoading ? (
      <Skeleton className="h-[48px] w-full rounded-[12px]" />
    ) : null;
  }

  const choices = programs.length
    ? programs
    : [{ orgSlug: current.orgSlug, orgName: current.orgName, isCurrent: true }];

  const pick = (orgSlug: string) => {
    if (orgSlug === current.orgSlug) return;
    setSwitchError(null);
    switchProgram.mutate({ ...access, orgSlug });
  };

  const error = switchError ? (
    // The role sits on the wrapper because the text roles take no `role` —
    // deliberately, so a heading cannot be announced as something else.
    <span role="alert">
      <Hint className="px-nf-ds-3 text-nf-ds-destructive">{switchError}</Hint>
    </span>
  ) : null;

  if (presentation === "sheet") {
    return (
      <>
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={sheetOpen}
          className={cn(
            "inline-flex h-[40px] min-w-0 shrink items-center gap-nf-ds-tight rounded-nf-ds-pill pr-nf-ds-2 pl-nf-ds-3",
            "ring-1 ring-nf-ds-surface-card-ring ring-inset outline-hidden",
            "focus-visible:ring-2 focus-visible:ring-nf-ds-brand",
            CONTROL_PRESS,
          )}
        >
          <Strong className="min-w-0 truncate text-nf-ds-hint">
            {current.orgName}
          </Strong>
          <ChevronDown
            aria-hidden="true"
            className="size-[16px] shrink-0 text-muted-foreground"
          />
        </button>

        <Sheet
          // The same recede the settings sheet gets. Two bottom sheets on one
          // surface where only one sinks the page is worse than neither.
          depthRootId={DEPTH_ROOT_ID}
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title={t("programs.sheetTitle")}
        >
          <div className="flex flex-col gap-nf-ds-item pt-nf-ds-1">
            <ListCard>
              {choices.map((program) => {
                const isCurrent = program.orgSlug === current.orgSlug;
                return (
                  <Row
                    key={program.orgSlug}
                    title={program.orgName}
                    value={
                      isCurrent ? (
                        <Check
                          aria-label={t("programs.current")}
                          className="size-[18px] text-nf-ds-brand"
                          strokeWidth={2.4}
                        />
                      ) : undefined
                    }
                    onClick={
                      isCurrent ? undefined : () => pick(program.orgSlug)
                    }
                  />
                );
              })}
            </ListCard>
            {/* The error stays in the sheet rather than in a popover that
                takes it away on dismiss — a switch that failed because access
                was removed needs somewhere to be read. */}
            {error}
            <Hint muted className="text-pretty">
              {t("programs.hint")}
            </Hint>
          </div>
        </Sheet>
      </>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-nf-ds-1">
      <Switcher
        value={current.orgSlug}
        onChange={pick}
        // The current program is always one of these, so a creator on a single
        // program still gets the tile, the name and the caption rather than a
        // bare line of text where the rail's other rows are. Naming the program
        // is half of what this control is for; switching is the other half, and
        // only one of the two needs a second program to be worth drawing.
        options={choices.map((program) => ({
          id: program.orgSlug,
          name: program.orgName,
        }))}
        label={t("programs.label")}
        searchPlaceholder={t("programs.searchPlaceholder")}
        searchLabel={t("programs.searchLabel")}
        // A filter over one row is a field asking you to type what you can
        // already see. Past that the default applies and it is always drawn,
        // because a search nobody can see is not a feature.
        filterAfter={1}
        // The rail carries it under the wordmark, so the list opens down.
        side="bottom"
        compact={compact}
      />
      {error}
    </div>
  );
}
